<script setup>
import { shared } from './shared.js'
</script>

<template>
    <div v-if="inrecDevice" class="card" style="margin-bottom: 20px;">
        <div class="card-header" style="background-color:#ffcdf7">
            <div class="form-check form-check-inline float-end" v-if="check_protocol()">
                <input class="form-check-input" type="checkbox" v-model="show_raw">
                <label class="form-label" style="margin-bottom: 0px;">Show raw</label>
            </div>
            <h5 style="margin-bottom: 0px;">
                <i v-if="inrecDevice.status == 'updating'" class="bi bi-cloud-arrow-down-fill text-primary"></i>
                <i v-else-if="check_online()" class="bi bi-activity text-primary"></i>
                <i v-else class="bi bi-heartbreak-fill text-danger"></i>
                InREC<span v-if="target != 'inrec'"> ({{ target.replace("inrec_", "") }})</span>
            </h5>
        </div>
        <div class="card-body" v-show="show_raw" style="padding: 0;">
            <Vue3JsonEditor class="json-raw-viewer" v-model="inrecDevice.data" :show-btns="false" :mode="'view'"
                :expandedOnStart="true" />
        </div>
        <div v-if="inrecDevice.status == 'updating'" class="card-body">
            <div class="alert alert-info" style="margin-bottom:0">Firmware update in progress</div>
        </div>
        <div v-else-if="check_protocol() && check_any_live_data()" class="card-body"
            :style="{ 'opacity': (check_online() && !shared.check_any_device_updating(deviceDetail) ? 100 : 25) + '%' }">
            <h5>Control</h5>
            <table class="table table-sm">
                <thead>
                    <tr>
                        <th></th>
                        <th width="100px"></th>
                        <th width="160px"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Power mode</td>
                        <td class="text-end">{{ get_data('power_mode.mode') }}</td>
                        <td v-if="shared.allow_control">
                            <div class="input-group input-group-sm">
                                <select v-if="check_minimum_firmware_version(2, 0) == false" v-model="target_power_mode"
                                    class="form-select">
                                    <option value="0">0: OFF</option>
                                    <option value="1">1: ACS</option>
                                    <option value="2">2: EXT_REF</option>
                                    <option value="3">3: I_REF</option>
                                    <option value="4">4: INVERTER_ONLY</option>
                                    <option value="5">5: CHARGING</option>
                                    <option value="6">6: UPS_2Q</option>
                                    <option value="7">7: UPS_4Q</option>
                                </select>
                                <select v-else v-model="target_power_mode" class="form-select">
                                    <option value="0">0: OFF</option>
                                    <option value="1">1: ACS</option>
                                    <option value="2">2: LOCAL_PREF</option>
                                </select>
                                <button type="button" class="btn btn-outline-danger"
                                    @click="writeParameter('power_mode.mode', target_power_mode)">></button>
                            </div>
                        </td>
                    </tr>
                    <tr
                        v-if="check_minimum_firmware_version(2, 0) == false && (target_power_mode == 2 || get_data('power_mode.mode') == 2)">
                        <td>External reference</td>
                        <td class="text-end">{{ get_data('power_mode.ext_ref') }} W</td>
                        <td v-if="shared.allow_control">
                            <div class="input-group input-group-sm">
                                <input class="form-control" v-model="target_ext_ref"
                                    v-on:keyup.enter="writeParameter('power_mode.ext_ref', target_ext_ref)" />
                                <button type="button" class="btn btn-outline-danger"
                                    @click="writeParameter('power_mode.ext_ref', target_ext_ref)">></button>
                            </div>
                        </td>
                    </tr>
                    <tr
                        v-if="check_minimum_firmware_version(2, 0) == true && (target_power_mode == 2 || get_data('power_mode.mode') == 2)">
                        <td>Local power reference (active)</td>
                        <td class="text-end">{{ inrecDevice.data.power_mode.pref }} W</td>
                        <td v-if="shared.allow_control">
                            <div class="input-group input-group-sm">
                                <input class="form-control" v-model="target_pref"
                                    v-on:keyup.enter="writeParameter('power_mode.pref', target_pref)" />
                                <button type="button" class="btn btn-outline-danger"
                                    @click="writeParameter('power_mode.pref', target_pref)">></button>
                            </div>
                        </td>
                    </tr>
                    <tr
                        v-if="check_minimum_firmware_version(2, 0) == true && (target_power_mode == 2 || get_data('power_mode.mode') == 2)">
                        <td>Local power reference (reactive)</td>
                        <td class="text-end">{{ inrecDevice.data.power_mode.qref }} var</td>
                        <td v-if="shared.allow_control">
                            <div class="input-group input-group-sm">
                                <input class="form-control" v-model="target_qref"
                                    v-on:keyup.enter="writeParameter('power_mode.qref', target_qref)" />
                                <button type="button" class="btn btn-outline-danger"
                                    @click="writeParameter('power_mode.qref', target_qref)">></button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table class="table table-sm text-end">
                <thead>
                    <tr>
                        <th width="250px"></th>
                        <th class="text-center" width="100px">L1</th>
                        <th class="text-center" width="100px">L2</th>
                        <th class="text-center" width="100px">L3</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="text-start">Reference (actual) - d</td>
                        <td>{{ get_data("system.iref.l1.d_actual", 2) }} A</td>
                        <td>{{ get_data("system.iref.l2.d_actual", 2) }} A</td>
                        <td>{{ get_data("system.iref.l3.d_actual", 2) }} A</td>
                    </tr>
                    <tr>
                        <td class="text-start">Reference (actual) - q</td>
                        <td>{{ get_data("system.iref.l1.q_actual", 2) }} A</td>
                        <td>{{ get_data("system.iref.l2.q_actual", 2) }} A</td>
                        <td>{{ get_data("system.iref.l3.q_actual", 2) }} A</td>
                    </tr>
                    <tr v-if="check_minimum_firmware_version(2, 0) == true" style="height:10px"></tr>
                    <tr v-if="check_minimum_firmware_version(2, 0) == true">
                        <td class="text-start">Cluster setpoint - active power</td>
                        <td>{{ get_data("cluster.pref_unit_actual.l1", 0) }} W</td>
                        <td>{{ get_data("cluster.pref_unit_actual.l2", 0) }} W</td>
                        <td>{{ get_data("cluster.pref_unit_actual.l3", 0) }} W</td>
                    </tr>
                    <tr v-if="check_minimum_firmware_version(2, 0) == true">
                        <td class="text-start">Cluster setpoint - reactive power</td>
                        <td>{{ get_data("cluster.qref_unit_actual.l1", 0) }} var</td>
                        <td>{{ get_data("cluster.qref_unit_actual.l2", 0) }} var</td>
                        <td>{{ get_data("cluster.qref_unit_actual.l3", 0) }} var</td>
                    </tr>
                </tbody>
            </table>

            <h5>System details</h5>
            <table class="table table-sm">
                <thead>
                    <tr>
                        <th width="250px"></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>State</td>
                        <td class="fw-bold">{{ get_descriptive_quick('system.state') }}</td>
                    </tr>
                    <tr>
                        <td>Status</td>
                        <td class="fw-bold">{{ get_descriptive_quick('system.status') }}</td>
                    </tr>
                    <tr>
                        <td>Warning</td>
                        <td class="fw-bold">{{ get_descriptive_quick('system.warning') }}</td>
                    </tr>
                    <tr>
                        <td>Error</td>
                        <td class="fw-bold">{{ get_descriptive_quick('system.error') }}</td>
                    </tr>
                    <tr>
                        <td>Critical</td>
                        <td>{{ get_descriptive('system.critical') }}</td>
                    </tr>
                    <tr>
                        <td>Hardware error</td>
                        <td>{{ get_descriptive('system.hwerror') }}</td>
                    </tr>
                    <tr>
                        <td>Multiplexer error cause</td>
                        <td>{{ get_descriptive('system.mux_overload_cause') }}</td>
                    </tr>
                    <tr>
                        <td>Cluster errors</td>
                        <td>{{ get_descriptive('cluster.errors') }}</td>
                    </tr>
                    <tr>
                        <td>Cluster error reason</td>
                        <td>{{ get_descriptive('cluster.error_reason') }}</td>
                    </tr>
                </tbody>
            </table>
            <h5>Measurements and references</h5>
            <table class="table table-sm text-end">
                <thead>
                    <tr>
                        <th></th>
                        <th class="text-center" width="100px">L1</th>
                        <th class="text-center" width="100px">L2</th>
                        <th class="text-center" width="100px">L3</th>
                        <th class="text-center" width="125px">Sum</th>
                    </tr>
                </thead>
                <tbody class="table-group-divider">
                    <tr>
                        <td class="text-start">Voltage (grid)</td>
                        <td>{{ get_data("avg.vgrid.L1", 1) }} V</td>
                        <td>{{ get_data("avg.vgrid.L2", 1) }} V</td>
                        <td>{{ get_data("avg.vgrid.L3", 1) }} V</td>
                        <td class="fw-bold">{{ get_data_quick("avg.fgrid", 2) }} Hz</td>
                    </tr>
                    <tr>
                        <td class="text-start">Voltage (inverter)</td>
                        <td class="fw-bold">{{ get_data_quick("avg.vinv.L1", 1) }} V</td>
                        <td class="fw-bold">{{ get_data_quick("avg.vinv.L2", 1) }} V</td>
                        <td class="fw-bold">{{ get_data_quick("avg.vinv.L3", 1) }} V</td>
                        <td></td>
                    </tr>
                    <tr>
                        <td class="text-start">Current</td>
                        <td class="fw-bold">{{ get_data_quick("avg.iinv.L1", 2) }} A</td>
                        <td class="fw-bold">{{ get_data_quick("avg.iinv.L2", 2) }} A</td>
                        <td class="fw-bold">{{ get_data_quick("avg.iinv.L3", 2) }} A</td>
                        <td></td>
                    </tr>
                    <tr>
                        <td class="text-start">Active power</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td class="fw-bold">{{ get_data_quick("avg.pgrid", 0)
                            }} W</td>
                    </tr>
                    <tr>
                        <td class="text-start">Reactive power</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td>{{ get_data("avg.qgrid", 0) }} var
                        </td>
                    </tr>
                    <tr>
                        <td class="text-start">Apparent power</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td>{{ get_data("avg.sgrid", 0) }} VA
                        </td>
                    </tr>
                </tbody>
            </table>
            <table class="table table-sm text-end">
                <thead>
                    <tr>
                        <th></th>
                        <th class="text-center" width="100px">DC1</th>
                        <th class="text-center" width="100px">DC2</th>
                        <th class="text-center" width="100px">DC3</th>
                        <th class="text-center" width="125px">Sum</th>
                    </tr>
                </thead>
                <tbody class="table-group-divider">
                    <tr>
                        <td class="text-start">Voltage</td>
                        <td class="fw-bold">{{ get_data_quick("avg.vdc1", 1) }} V</td>
                        <td class="fw-bold">{{ get_data_quick("avg.vdc2", 1) }} V</td>
                        <td class="fw-bold">{{ get_data_quick("avg.vdc3", 1) }} V</td>
                        <td></td>
                    </tr>
                    <tr>
                        <td class="text-start">Current</td>
                        <td class="fw-bold">{{ get_data_quick("avg.idc1", 2) }} A</td>
                        <td class="fw-bold">{{ get_data_quick("avg.idc2", 2) }} A</td>
                        <td class="fw-bold">{{ get_data_quick("avg.idc3", 2) }} A</td>
                        <td>{{ get_data("avg.idc_sum", 2) }} A
                        </td>
                    </tr>
                    <tr>
                        <td class="text-start">Power</td>
                        <td>{{ get_data("avg.pdc1", 0) }} W</td>
                        <td>{{ get_data("avg.pdc2", 0) }} W</td>
                        <td>{{ get_data("avg.pdc3", 0) }} W</td>
                        <td class="fw-bold">{{ get_data_quick("avg.pdc", 0) }} W</td>
                    </tr>
                    <tr style="height:15px">

                    </tr>

                    <tr>
                        <td class="text-start">DC link voltage</td>
                        <td><small><i>Center tap</i></small></td>
                        <td class="fw-bold"><small>{{ get_data_quick("avg.vbus2", 1) }} V </small></td>
                        <td></td>
                        <td class="fw-bold">{{ get_data_quick("avg.vbus", 1) }} V </td>
                    </tr>
                </tbody>
            </table>
            <span>
                <h5>BMS</h5>
                <table class="table table-sm text-end">
                    <thead>
                        <tr>
                            <th></th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="text-start">State of Charge</td>
                            <td>{{ get_data("bms.soc", 1) }} %</td>
                            <td class="text-end">State of Health</td>
                            <td>{{ get_data("bms.soh", 1) }} %</td>
                        </tr>
                        <tr>
                            <td class="text-start">BMS select</td>
                            <td>{{ get_data("bms.bms_select", 0) }}</td>
                            <td class="text-end">BMS manual override</td>
                            <td>{{ get_data("bms.manual_override", 0) }}</td>
                        </tr>
                    </tbody>
                </table>
                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th></th>
                            <th>DC</th>
                            <th>DC1</th>
                            <th>DC2</th>
                            <th>DC3</th>
                            <th>AC</th>
                        </tr>
                    </thead>
                    <tbody class="table-group-divider">
                        <tr>
                            <td>Charge limits</td>
                            <td><small>{{ get_data("bms.ichg.max", 1) }} A</small></td>
                            <td><small>{{ get_data("bms.ichg.max_1", 1) }} A</small></td>
                            <td><small>{{ get_data("bms.ichg.max_2", 1) }} A</small></td>
                            <td><small>{{ get_data("bms.ichg.max_3", 1) }} A</small></td>
                            <td><small>{{ get_data("bms.irefchg.max", 1) }} A</small></td>
                        </tr>
                        <tr>
                            <td>Discharge limits</td>
                            <td><small>{{ get_data("bms.idischg.max", 1) }} A</small></td>
                            <td><small>{{ get_data("bms.idischg.max_1", 1) }} A</small></td>
                            <td><small>{{ get_data("bms.idischg.max_2", 1) }} A</small></td>
                            <td><small>{{ get_data("bms.idischg.max_3", 1) }} A</small></td>
                            <td><small>{{ get_data("bms.irefdischg.max", 1) }} A</small></td>
                        </tr>
                    </tbody>
                </table>
            </span>
            <h5>Temperatures</h5>
            <table class="table table-sm text-end">
                <thead>
                    <tr>
                        <th width="200px"></th>
                        <th width="200px"></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="text-start">Board</td>
                        <td>{{ get_data("avg.tboard", 1) }} °C</td>
                    </tr>
                    <tr>
                        <td class="text-start">DCDC module</td>
                        <td>{{ get_data("avg.tmod.dc", 1) }} °C</td>
                    </tr>
                    <tr>
                        <td class="text-start">DCAC module L1</td>
                        <td>{{ get_data("avg.tmod.ac1", 1) }} °C</td>
                    </tr>
                    <tr>
                        <td class="text-start">DCAC module L2</td>
                        <td>{{ get_data("avg.tmod.ac2", 1) }} °C</td>
                    </tr>
                    <tr>
                        <td class="text-start">DCAC module L3</td>
                        <td>{{ get_data("avg.tmod.ac3", 1) }} °C</td>
                    </tr>
                    <tr>
                        <td class="text-start">DC common-mode choke</td>
                        <td>{{ get_data("avg.tcap", 1) }} °C</td>
                    </tr>
                    <tr>
                        <td class="text-start">Long tray (DC3/AC1)</td>
                        <td>{{ get_data("avg.tcoil", 1) }} °C</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-else class="card-body">
            <div class="alert alert-warning" style="margin-bottom:0">No data</div>
        </div>
    </div>
</template>

<script>
export default {
    props: ['inrecDevice', 'deviceDetail', 'client', 'target'],
    setup(props) { },
    data() {
        return {
            show_raw: false,
            target_power_mode: 0,
            target_pref: 0,
            target_qref: 0,
            target_iref_d: 0,
            target_iref_q: 0,
        };
    },
    methods: {
        check_minimum_firmware_version(major, minor) {
            // Check if firmware is above certain version
            let version_minimum = (major << 24) | (minor << 16);
            let current_version = shared.get_nested_value_safely(this.inrecDevice.data, "version.firmware", 0)
            if (current_version == "-" || parseInt(current_version) < version_minimum) {
                return false;
            } else {
                return true;
            }
        },
        check_protocol() {
            return this.deviceDetail.info && this.deviceDetail.info.protocol_version != null
        },
        check_online() {
            return shared.check_device_online(this.inrecDevice)
        },
        check_any_live_data() {
            return shared.check_any_live_data(this.inrecDevice)
        },
        get_data(parameter, round = null) {
            return shared.get_nested_value_safely(this.inrecDevice.data, parameter, round)
        },
        get_data_quick(parameter, round = null) {
            return shared.get_nested_value_safely(this.inrecDevice.data_quick, parameter, round)
        },
        get_descriptive(parameter) {
            return shared.get_descriptive_safely(this.inrecDevice.data, parameter)
        },
        get_descriptive_quick(parameter) {
            return shared.get_descriptive_safely(this.inrecDevice.data_quick, parameter)
        },
        writeParameter(parameter, value) {
            var topic = this.deviceDetail.topic_base + "/" + this.deviceDetail.serial + "/request"
            var payload = {
                function: "write_parameter",
                target: this.target,
                parameter: parameter,
                value: value,
            }
            console.log({ topic: topic, payload: payload })
            this.client.publish(topic, JSON.stringify(payload), error => {
                if (error) {
                    console.log('Publish error', error)
                }
            })
        }
    },
    created() {
        if (this.deviceDetail.info.protocol_version == null) {
            this.show_raw = 1;
        }
    },
};
</script>